import React, { useState, useRef, useEffect } from 'react';
import { Plus, X, Play, Pause, SkipForward, SkipBack, Volume2, VolumeX } from 'lucide-react';
import { styles } from './styles.js';

function CountdownTimer({ timers, setTimers }) {
  const [newTimerName, setNewTimerName] = useState('');
  const [newTimerType, setNewTimerType] = useState('study');
  const [newTimerHours, setNewTimerHours] = useState(0);
  const [newTimerMinutes, setNewTimerMinutes] = useState(25);
  const [newTimerSeconds, setNewTimerSeconds] = useState(0);
  const [customDeadline, setCustomDeadline] = useState('');
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [nameError, setNameError] = useState('');

  // Check if device is mobile
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };
    
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const timerTypes = {
    study: { name: 'Study Session', icon: '📚', color: '#3b82f6' },
    break: { name: 'Break Time', icon: '☕', color: '#10b981' },
    pomodoro: { name: 'Pomodoro', icon: '🍅', color: '#ef4444' },
    exam: { name: 'Exam Countdown', icon: '📝', color: '#f59e0b' },
    assignment: { name: 'Assignment Due', icon: '📋', color: '#8b5cf6' },
    custom: { name: 'Custom Timer', icon: '⏰', color: '#6b7280' }
  };

  // Mobile-optimized styles
  const getMobileStyles = () => ({
    header: {
      ...styles.todoHeader,
      padding: isMobile ? '12px' : styles.todoHeader.padding
    },
    headerContent: {
      display: 'flex',
      flexDirection: isMobile ? 'column' : 'row',
      justifyContent: 'space-between',
      alignItems: isMobile ? 'stretch' : 'center',
      marginBottom: '16px',
      gap: isMobile ? '12px' : '0'
    },
    stats: {
      ...styles.stats,
      fontSize: isMobile ? '12px' : styles.stats.fontSize,
      gap: isMobile ? '8px' : '16px',
      justifyContent: isMobile ? 'space-around' : 'flex-start'
    },
    nameInput: {
      ...styles.input,
      width: '100%',
      minWidth: isMobile ? '100%' : '500px',
      height: '20px',
      resize: 'none',
      fontFamily: 'inherit',
      marginBottom: '20px',
      ...(nameError ? { borderColor: '#ef4444', borderWidth: '2px' } : {})
    },
    controlsRow: {
      display: 'flex',
      gap: isMobile ? '8px' : '12px',
      marginBottom: '12px',
      flexDirection: isMobile ? 'column' : 'row',
      alignItems: isMobile ? 'stretch' : 'center'
    },
    typeSelect: {
      ...styles.select,
      minWidth: isMobile ? '100%' : '150px',
      marginBottom: isMobile ? '8px' : '0'
    },
    timeInputs: {
      display: 'flex',
      gap: '8px',
      alignItems: 'center',
      justifyContent: isMobile ? 'center' : 'flex-start',
      flex: isMobile ? 'none' : 1
    },
    timeInput: {
      ...styles.input,
      width: isMobile ? '50px' : '60px',
      textAlign: 'center'
    },
    deadlineInput: {
      ...styles.input,
      flex: 1,
      minWidth: isMobile ? '100%' : '200px'
    },
    createButton: {
      ...styles.addButton,
      minWidth: isMobile ? '100%' : 'auto',
      justifyContent: 'center',
      marginTop: isMobile ? '8px' : '0'
    },
    presetsContainer: {
      display: 'flex',
      gap: '8px',
      flexWrap: 'wrap',
      alignItems: 'center'
    },
    presetButton: {
      padding: isMobile ? '6px 10px' : '4px 8px',
      fontSize: '12px',
      backgroundColor: '#f3f4f6',
      border: '1px solid #d1d5db',
      borderRadius: '4px',
      cursor: 'pointer',
      textAlign: 'center'
    },
    timerCard: {
      ...styles.taskItem,
      padding: isMobile ? '12px' : styles.taskItem.padding
    },
    timerHeader: {
      display: 'flex',
      flexDirection: isMobile ? 'column' : 'row',
      alignItems: isMobile ? 'stretch' : 'center',
      justifyContent: 'space-between',
      gap: isMobile ? '12px' : '0',
      marginBottom: '12px'
    },
    timerInfo: {
      display: 'flex',
      alignItems: 'center',
      gap: '12px',
      marginBottom: isMobile ? '8px' : '0'
    },
    timerControls: {
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
      justifyContent: isMobile ? 'center' : 'flex-end',
      flexWrap: isMobile ? 'wrap' : 'nowrap'
    },
    controlButton: {
      padding: isMobile ? '10px 16px' : '8px 16px',
      border: 'none',
      borderRadius: '4px',
      cursor: 'pointer',
      fontSize: '14px',
      minWidth: isMobile ? '80px' : 'auto'
    },
    timeDisplay: {
      fontSize: isMobile ? (timer => timer.timeLeft > 3600 ? '28px' : '36px') : (timer => timer.timeLeft > 3600 ? '36px' : '48px'),
      fontWeight: 'bold',
      marginBottom: '8px',
      textAlign: 'center'
    },
    errorMessage: {
      color: '#ef4444',
      fontSize: '14px',
      marginTop: '4px',
      marginBottom: '16px'
    }
  });

  // Additional styles specific to clear data button
  const clearDataButtonStyle = {
    padding: '6px 12px',
    backgroundColor: '#dc2626',
    color: 'white',
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
    fontSize: isMobile ? '11px' : '12px',
    marginLeft: isMobile ? '0' : '8px',
    marginTop: isMobile ? '4px' : '0'
  };

  const mobileStyles = getMobileStyles();

  // Clear all saved data function
  const clearAllData = () => {
    if (window.confirm('Are you sure you want to clear all saved timer data? This action cannot be undone.')) {
      try {
        localStorage.removeItem('countdownTimers');
        
        // Reset all state
        setTimers([]);
        setNewTimerName('');
        setNewTimerType('study');
        setNewTimerHours(0);
        setNewTimerMinutes(25);
        setNewTimerSeconds(0);
        setCustomDeadline('');
        
        alert('All timer data cleared successfully!');
      } catch (error) {
        console.error('Error clearing data:', error);
        alert('Error clearing data. Please try again.');
      }
    }
  };

  // ✅ Load timers from localStorage on mount
  useEffect(() => {
    const savedTimers = localStorage.getItem('countdownTimers');
    if (savedTimers) {
      try {
        const parsed = JSON.parse(savedTimers);
        const now = Date.now();

        const updated = parsed.map(timer => {
          if (timer.endTime && timer.isRunning) {
            const newTimeLeft = Math.max(0, Math.floor((timer.endTime - now) / 1000));
            
            // Check if timer finished while away
            if (newTimeLeft === 0 && timer.timeLeft > 0) {
              return {
                ...timer,
                timeLeft: 0,
                isRunning: false,
                isFinished: true
              };
            }
            
            return {
              ...timer,
              timeLeft: newTimeLeft,
              isRunning: newTimeLeft > 0
            };
          }
          return timer;
        });

        setTimers(updated);
      } catch (error) {
        console.error('Error loading timers from localStorage:', error);
      }
    }
  }, [setTimers]);

  // ✅ Save timers to localStorage whenever they change
  useEffect(() => {
    try {
      localStorage.setItem('countdownTimers', JSON.stringify(timers));
    } catch (error) {
      console.error('Error saving timers to localStorage:', error);
    }
  }, [timers]);

  // Update timers every second
  React.useEffect(() => {
    const interval = setInterval(() => {
      setTimers(prevTimers => 
        prevTimers.map(timer => {
          if (timer.isRunning && timer.timeLeft > 0 && timer.endTime) {
            const now = Date.now();
            const newTimeLeft = Math.max(0, Math.floor((timer.endTime - now) / 1000));
            
            // Check if timer finished
            if (newTimeLeft === 0) {
              // Show notification
              if ('Notification' in window && Notification.permission === 'granted') {
                new Notification(`Timer Finished: ${timer.name}`, {
                  body: `Your ${timerTypes[timer.type].name.toLowerCase()} timer has ended!`,
                  icon: timerTypes[timer.type].icon
                });
              }
              
              // Play sound (if browser supports it)
              try {
                const audio = new Audio('data:audio/wav;base64,UklGRnoGAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQoGAACBhYqFbF1fdJivrJBhNjVgodDbq2EcBj+a2/LDciUFLIHO8tiJNwgZaLvt559NEAxQp+PwtmMcBjiR1/LMeSwFJHfH8N2QQAoUXrTp66hVFApGn+DyvmwhBKi89qJlGwU5idDyzHkpBS11xe7dkUIKFGG66eewYBsGPY/Y88p6KwUmgcP1/Zg2ChZYsOPrr1oUCEOd4PO7cSoErrPymGQdBDOR0vHNeCsFJHfH8N2QQAoUXrTp66hVFApGn+DyvmwhBKi89qJlGwU5idDyzHkpBS11xe7dkUIK');
                audio.play().catch(() => {/* ignore errors */});
              } catch (e) {/* ignore errors */}
              
              return { ...timer, isRunning: false, timeLeft: 0, isFinished: true, endTime: null };
            }
            
            return { ...timer, timeLeft: newTimeLeft };
          }
          return timer;
        })
      );
    }, 1000);

    return () => clearInterval(interval);
  }, [setTimers, timerTypes]);

  // Request notification permission
  React.useEffect(() => {
    if ('Notification' in window && Notification.permission === 'default') {
      Notification.requestPermission();
    }
  }, []);

  const formatTime = (seconds) => {
    const hours = Math.floor(seconds / 3600);
    const mins = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    
    if (hours > 0) {
      return `${hours}:${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
    }
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const createTimer = () => {
    // Clear any previous error message
    setNameError('');
    
    if (!newTimerName.trim()) {
      setNameError('Please enter a timer name before creating the timer.');
      return;
    }

    let totalSeconds;
    if (newTimerType === 'exam' || newTimerType === 'assignment') {
      if (!customDeadline) return;
      const deadline = new Date(customDeadline);
      const now = new Date();
      totalSeconds = Math.max(0, Math.floor((deadline - now) / 1000));
    } else {
      totalSeconds = (newTimerHours * 3600) + (newTimerMinutes * 60) + newTimerSeconds;
    }

    if (totalSeconds <= 0) return;

    const timer = {
      id: Date.now(),
      name: newTimerName.trim(),
      type: newTimerType,
      originalTime: totalSeconds,
      timeLeft: totalSeconds,
      isRunning: false,
      isFinished: false,
      endTime: null,
      createdAt: new Date().toLocaleString()
    };

    setTimers([timer, ...timers]);
    setNewTimerName('');
    setNewTimerHours(0);
    setNewTimerMinutes(25);
    setNewTimerSeconds(0);
    setCustomDeadline('');
  };

  const toggleTimer = (id) => {
    setTimers(timers.map(timer => {
      if (timer.id === id) {
        if (timer.isRunning) {
          // Pause timer
          return { 
            ...timer, 
            isRunning: false,
            endTime: null
          };
        } else {
          // Start/resume timer
          const now = Date.now();
          return { 
            ...timer, 
            isRunning: true,
            endTime: now + (timer.timeLeft * 1000)
          };
        }
      }
      return timer;
    }));
  };

  const resetTimer = (id) => {
    setTimers(timers.map(timer =>
      timer.id === id ? { 
        ...timer, 
        timeLeft: timer.originalTime, 
        isRunning: false, 
        isFinished: false,
        endTime: null
      } : timer
    ));
  };

  const deleteTimer = (id) => {
    setTimers(timers.filter(timer => timer.id !== id));
  };

  const getProgressPercentage = (timer) => {
    return ((timer.originalTime - timer.timeLeft) / timer.originalTime) * 100;
  };

  return (
    <div style={styles.todoContainer}>
      <div style={mobileStyles.header}>
        <div style={mobileStyles.headerContent}>
          <h2 style={styles.todoTitle}>⏰ Countdown Timer</h2>
          <div style={{display: 'flex', flexDirection: 'row', alignItems: 'center', gap: '8px'}}>
            <span style={{fontSize: '12px', color: '#10b981'}}>✓ Auto-saved</span>
            <button onClick={clearAllData} style={clearDataButtonStyle}>
              🗑️ Clear All Data
            </button>
          </div>
        </div>
        <div style={mobileStyles.stats}>
          <span>Active: {timers.filter(t => t.isRunning).length}</span>
          <span style={{color: '#10b981'}}>Finished: {timers.filter(t => t.isFinished).length}</span>
          <span style={{color: '#f59e0b'}}>Total: {timers.length}</span>
        </div>
      </div>

      <div style={styles.addTaskSection}>
        <div style={{marginBottom: '16px'}}>
          <input
            type="text"
            value={newTimerName}
            onChange={(e) => {
              setNewTimerName(e.target.value);
              if (nameError && e.target.value.trim()) {
                setNameError('');
              }
            }}
            placeholder="Timer name (e.g., 'Math Study Session')"
            style={mobileStyles.nameInput}
            onKeyPress={(e) => e.key === 'Enter' && createTimer()}
          />
          {nameError && (
            <div style={mobileStyles.errorMessage}>
              {nameError}
            </div>
          )}
          
          <div style={mobileStyles.controlsRow}>
            <select
              value={newTimerType}
              onChange={(e) => setNewTimerType(e.target.value)}
              style={mobileStyles.typeSelect}
            >
              {Object.entries(timerTypes).map(([key, type]) => (
                <option key={key} value={key}>{type.name}</option>
              ))}
            </select>

            {(newTimerType === 'exam' || newTimerType === 'assignment') ? (
              <div style={{flex: 1, minWidth: isMobile ? '100%' : '200px'}}>
                <input
                  type="datetime-local"
                  value={customDeadline}
                  onChange={(e) => setCustomDeadline(e.target.value)}
                  style={mobileStyles.deadlineInput}
                  min={new Date().toISOString().slice(0, 16)}
                />
              </div>
            ) : (
              <div style={mobileStyles.timeInputs}>
                <input
                  type="number"
                  min="0"
                  max="23"
                  value={newTimerHours}
                  onChange={(e) => setNewTimerHours(parseInt(e.target.value) || 0)}
                  style={mobileStyles.timeInput}
                  placeholder="0"
                />
                <span style={{fontSize: '14px', color: '#6b7280'}}>h</span>
                
                <input
                  type="number"
                  min="0"
                  max="59"
                  value={newTimerMinutes}
                  onChange={(e) => setNewTimerMinutes(parseInt(e.target.value) || 0)}
                  style={mobileStyles.timeInput}
                  placeholder="25"
                />
                <span style={{fontSize: '14px', color: '#6b7280'}}>m</span>
                
                <input
                  type="number"
                  min="0"
                  max="59"
                  value={newTimerSeconds}
                  onChange={(e) => setNewTimerSeconds(parseInt(e.target.value) || 0)}
                  style={mobileStyles.timeInput}
                  placeholder="0"
                />
                <span style={{fontSize: '14px', color: '#6b7280'}}>s</span>
              </div>
            )}

            <button onClick={createTimer} style={mobileStyles.createButton}>
              <Plus style={{width: '16px', height: '16px'}} />
              Create Timer
            </button>
          </div>
        </div>

        {/* Quick Preset Buttons */}
        <div style={mobileStyles.presetsContainer}>
          <span style={{fontSize: '12px', color: '#6b7280', alignSelf: 'center', width: isMobile ? '100%' : 'auto', textAlign: isMobile ? 'center' : 'left', marginBottom: isMobile ? '8px' : '0'}}>Quick presets:</span>
          <div style={{display: 'flex', gap: '8px', flexWrap: 'wrap', justifyContent: isMobile ? 'center' : 'flex-start', width: isMobile ? '100%' : 'auto'}}>
            {[
              { name: 'Pomodoro', type: 'pomodoro', time: [0, 25, 0] },
              { name: 'Short Break', type: 'break', time: [0, 5, 0] },
              { name: 'Long Break', type: 'break', time: [0, 15, 0] },
              { name: '1 Hour Study', type: 'study', time: [1, 0, 0] }
            ].map((preset) => (
              <button
                key={preset.name}
                onClick={() => {
                  setNewTimerName(preset.name);
                  setNewTimerType(preset.type);
                  setNewTimerHours(preset.time[0]);
                  setNewTimerMinutes(preset.time[1]);
                  setNewTimerSeconds(preset.time[2]);
                  setNameError('');
                }}
                style={mobileStyles.presetButton}
              >
                {preset.name}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div style={styles.taskList}>
        {timers.length === 0 ? (
          <div style={styles.emptyState}>
            No timers yet. Create your first timer above!
          </div>
        ) : (
          timers.map((timer) => {
            const timerType = timerTypes[timer.type];
            const progress = getProgressPercentage(timer);
            
            return (
              <div
                key={timer.id}
                style={{
                  ...mobileStyles.timerCard,
                  borderLeft: `4px solid ${timerType.color}`,
                  ...(timer.isFinished ? {backgroundColor: '#f0fdf4', borderColor: '#bbf7d0'} : {}),
                  ...(timer.isRunning ? {backgroundColor: '#fef3c7', borderColor: '#fcd34d'} : {})
                }}
              >
                <div style={{...styles.taskContent, flexDirection: 'column', alignItems: 'stretch', gap: '12px'}}>
                  <div style={mobileStyles.timerHeader}>
                    <div style={mobileStyles.timerInfo}>
                      <span style={{fontSize: '24px'}}>{timerType.icon}</span>
                      <div>
                        <div style={{fontSize: '18px', fontWeight: '500', color: '#1f2937'}}>
                          {timer.name}
                        </div>
                        <div style={{fontSize: '12px', color: '#6b7280'}}>
                          {timerType.name} • Created: {timer.createdAt}
                        </div>
                      </div>
                    </div>
                    
                    <div style={mobileStyles.timerControls}>
                      {!timer.isFinished && (
                        <>
                          <button
                            onClick={() => toggleTimer(timer.id)}
                            style={{
                              ...mobileStyles.controlButton,
                              backgroundColor: timer.isRunning ? '#ef4444' : '#10b981',
                              color: 'white'
                            }}
                          >
                            {timer.isRunning ? 'Pause' : 'Start'}
                          </button>
                          
                          <button
                            onClick={() => resetTimer(timer.id)}
                            style={{
                              ...mobileStyles.controlButton,
                              backgroundColor: '#6b7280',
                              color: 'white'
                            }}
                          >
                            Reset
                          </button>
                        </>
                      )}
                      
                      <button
                        onClick={() => deleteTimer(timer.id)}
                        style={{
                          ...styles.deleteButton,
                          minWidth: isMobile ? '40px' : 'auto'
                        }}
                      >
                        <X style={{width: '16px', height: '16px'}} />
                      </button>
                    </div>
                  </div>
                  
                  <div style={{textAlign: 'center'}}>
                    <div style={{
                      fontSize: isMobile ? (timer.timeLeft > 3600 ? '28px' : '36px') : (timer.timeLeft > 3600 ? '36px' : '48px'),
                      fontWeight: 'bold',
                      color: timer.isFinished ? '#10b981' : timer.timeLeft <= 60 ? '#ef4444' : '#1f2937',
                      marginBottom: '8px'
                    }}>
                      {timer.isFinished ? '✅ FINISHED!' : formatTime(timer.timeLeft)}
                    </div>
                    
                    {!timer.isFinished && (
                      <div style={{
                        width: '100%',
                        height: '8px',
                        backgroundColor: '#e5e7eb',
                        borderRadius: '4px',
                        overflow: 'hidden'
                      }}>
                        <div
                          style={{
                            height: '100%',
                            backgroundColor: timerType.color,
                            width: `${progress}%`,
                            transition: 'width 1s linear'
                          }}
                        />
                      </div>
                    )}
                  </div>
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}

export default CountdownTimer;